import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { storage } from '@/lib/storage';
import { useStudio } from '@/store/StudioStore';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [history, setHistory] = useState([]);
  const { dispatch } = useStudio();
  const nav = useNavigate();
  const fileRef = useRef(null);

  useEffect(() => { setProjects(storage.listProjects()); setHistory(storage.listHistory()); }, []);

  const open = (p) => { dispatch({ type: 'LOAD', project: p }); nav('/studio'); };
  const del = (id) => { if (confirm('Delete this project?')) { storage.deleteProject(id); setProjects(storage.listProjects()); } };
  const exp = (p) => storage.exportProject(p);
  const importJSON = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = (evt) => {
      try {
        const p = JSON.parse(evt.target.result);
        storage.saveProject(p);
        setProjects(storage.listProjects());
        toast.success('Project imported.');
      } catch { toast.error('Invalid JSON'); }
    };
    r.readAsText(file);
  };

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10" data-testid="projects-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow">Browser Local</div>
          <h1 className="font-display text-5xl">My Projects</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="import-project" onClick={() => fileRef.current?.click()} className="chrome-border chrome-hover rounded-full px-5 py-2 text-xs uppercase tracking-widest">Import JSON</button>
          <input ref={fileRef} type="file" accept="application/json" onChange={importJSON} className="hidden" />
          <button data-testid="new-project" onClick={() => { dispatch({ type: 'RESET' }); nav('/studio'); }} className="btn-magenta rounded-full px-5 py-2 text-xs uppercase tracking-widest">New Project</button>
        </div>
      </div>

      <p className="text-xs text-zinc-500 mb-6">Saved in your browser only. Clearing browser data will remove them — export important recipes as JSON.</p>

      {projects.length === 0 && <div className="text-zinc-500 text-sm">No saved projects yet. Build one in the studio and hit &ldquo;Save Project&rdquo;.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="rounded-2xl chrome-border bg-zinc-900/60 p-5" data-testid={`project-${p.id}`}>
            <div className="eyebrow">{new Date(p.savedAt || p.createdAt).toLocaleString()}</div>
            <h3 className="font-display text-2xl mt-1 truncate">&ldquo;{p.exactText || p.name}&rdquo;</h3>
            <div className="text-xs text-zinc-400 mt-2 truncate">{p.creationTypeLabel} · {p.topicItem || p.topicCategoryLabel || '—'}</div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => open(p)} data-testid={`open-project-${p.id}`} className="btn-magenta rounded-full px-4 py-2 text-xs uppercase tracking-widest flex-1">Open</button>
              <button onClick={() => exp(p)} className="chrome-border rounded-full px-3 py-2 text-xs uppercase tracking-widest">Export</button>
              <button onClick={() => del(p.id)} className="chrome-border rounded-full px-3 py-2 text-xs uppercase tracking-widest text-pink-500">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <div className="mt-14">
          <div className="eyebrow mb-2">Recent Prompt History</div>
          <div className="rounded-2xl chrome-border bg-zinc-900/60">
            {history.slice(0, 15).map((h) => (
              <div key={h.id} className="flex items-center justify-between px-4 py-2 border-b border-white/5 last:border-b-0" data-testid={`history-${h.id}`}>
                <div className="text-sm truncate">&ldquo;{h.name}&rdquo;</div>
                <div className="text-xs text-zinc-500">{new Date(h.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
